import { useCallback, useEffect, useState } from "react";
import { isChatEscalation, type HandoffDto } from "@care-by-ella/contracts";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { handoffSenderLine } from "@/lib/handoff-labels";
import { SupportEscalationMessages } from "./SupportEscalationMessages";
import {
  acknowledgeHandoff,
  listHandoffQueue,
  resolveHandoff,
  startHandoff,
} from "@/lib/handoff-api";
import { levelLabel } from "@/lib/state-labels";
import { useSupportView } from "./SupportView";

// TES-519 — the open handoffs ON the patient screen, with their actions.
//
// The "Överlämningar" lane used to point at /handoffs, a page of its own.
// Every lane row lands on the patient screen with the
// content up and one clear CTA. So the row now lands here (`#handoff`), and
// the CTA is the next step in the handoff's own lifecycle: acknowledge →
// start → resolve. PatientHandoff below this stays the CREATE form.
//
// Self-fetching (the role's queue, filtered to this patient) rather than a
// new field on the patient DTO: the queue endpoint already scopes by role and
// is what the lane itself reads, so the two can never disagree.

const STATUS_LABEL: Record<string, string> = {
  sent: "Skickad",
  acknowledged: "Bekräftad",
  in_progress: "Pågår",
  resolved: "Löst",
};

export function PatientOpenHandoffs({ patientId }: { patientId: string }) {
  const { claims } = useAuth();
  // TES-1040 — in the admin's read-only view: the handoffs of the view she
  // is in (doctor or coach), without any of the buttons that move them on.
  const support = useSupportView();
  const readOnly = support !== null;
  const role =
    (support?.viewAs ?? claims?.role) === "coach" ? "coach" : "doctor";
  const [rows, setRows] = useState<HandoffDto[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolution, setResolution] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    listHandoffQueue(role)
      .then((all) =>
        setRows(
          all.filter(
            (h) => h.patientId === patientId && h.status !== "resolved",
          ),
        ),
      )
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [patientId, role]);

  useEffect(load, [load]);

  // TES-1034 — the work-queue row lands here with `#handoff`. The router does
  // not scroll to a hash, and this section renders only once its rows are
  // in, so it brings itself into view when it is the one asked for.
  const { hash } = useLocation();
  const hasRows = !!rows && rows.length > 0;
  useEffect(() => {
    if (hash !== "#handoff" || !hasRows) return;
    document
      .getElementById("handoff")
      ?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [hash, hasRows]);

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id);
    setError(null);
    try {
      await fn();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (rows === null || rows.length === 0) return null;

  return (
    <section
      id="handoff"
      className="rounded-md border border-brand/40 bg-surface p-md"
    >
      <h2 className="mb-sm font-display text-lg">
        Överlämningar till dig ({rows.length})
      </h2>
      {error ? <p className="mb-sm text-sm text-danger">{error}</p> : null}
      <ul className="flex flex-col gap-sm">
        {rows.map((h) => (
          <li
            key={h.id}
            className="rounded-sm border border-surfaceMuted p-sm text-sm"
          >
            <div className="flex flex-wrap items-baseline gap-sm">
              <span className="font-medium">{h.reason}</span>
              <span className="text-xs text-textSecondary">
                {h.status === "resolved" && isChatEscalation(h)
                  ? "Bedömt av läkare"
                  : (STATUS_LABEL[h.status] ?? h.status)}{" "}
                · {levelLabel(h.priority)}
                {h.deadline ? ` · senast ${h.deadline.slice(0, 10)}` : ""}
              </span>
            </div>
            {/* TES-1034 (Emelie's point 8) — who sent it, and when. */}
            <p className="mt-xs text-xs text-textSecondary">
              {handoffSenderLine(h)}
            </p>
            {h.context ? (
              <p className="mt-xs whitespace-pre-wrap text-textSecondary">
                {h.context}
              </p>
            ) : null}
            {h.requestedDecision ? (
              <p className="mt-xs text-textSecondary">
                <span className="font-semibold">Efterfrågat beslut: </span>
                {h.requestedDecision}
              </p>
            ) : null}
            {/* TES-1110 — escalated from Ella Support: the message is linked,
                not copied. */}
            <div className="mt-xs">
              <SupportEscalationMessages handoff={h} readOnly={readOnly} />
            </div>
            {readOnly ? null : (
              <div className="mt-sm flex flex-wrap items-center gap-sm">
                {h.status === "sent" ? (
                  <button
                    type="button"
                    disabled={busy === h.id}
                    onClick={() =>
                      void act(h.id, () => acknowledgeHandoff(h.id))
                    }
                    className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
                  >
                    Bekräfta
                  </button>
                ) : null}
                {h.status === "acknowledged" ? (
                  <button
                    type="button"
                    disabled={busy === h.id}
                    onClick={() => void act(h.id, () => startHandoff(h.id))}
                    className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
                  >
                    Påbörja
                  </button>
                ) : null}
                {h.status === "in_progress" ? (
                  <>
                    <input
                      value={resolution[h.id] ?? ""}
                      onChange={(e) =>
                        setResolution((r) => ({ ...r, [h.id]: e.target.value }))
                      }
                      placeholder="Vad gjordes?"
                      className="min-w-[16rem] flex-1 rounded-md border border-surfaceMuted bg-background px-sm py-xs text-sm"
                    />
                    <button
                      type="button"
                      disabled={
                        busy === h.id || !(resolution[h.id] ?? "").trim()
                      }
                      onClick={() =>
                        void act(h.id, () =>
                          resolveHandoff(h.id, (resolution[h.id] ?? "").trim()),
                        )
                      }
                      className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
                    >
                      Markera som löst
                    </button>
                  </>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
